// frontend/src/modules/marginCenter/pages/MarginCenterOverviewPage.tsx

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import OverviewEmptyState from '../../pricingJourney/components/OverviewEmptyState';
import PageHeader, { pageFrameSx } from '../../pricingJourney/components/PageHeader';
import { withReturnTo } from '../../pricingJourney/navigation';
import { useMarkOnboardingEvent } from '../../pricingJourney/onboarding';
import PageSkeleton from '../../pricingJourney/components/Skeletons';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Container,
  LinearProgress,
  Paper,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import NotificationsNoneRoundedIcon
  from '@mui/icons-material/NotificationsNoneRounded';
import SearchRoundedIcon
  from '@mui/icons-material/SearchRounded';

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getMarginCenterCadenceAttention,
  getMarginCenterCapabilities,
  getMarginCenterExplorer,
  getMarginCenterFollowUpAttention,
  getMarginCenterFollowUpPortfolio,
  getMarginCenterOverview,
} from '../services/marginCenterService';

import CommercialQueryPanel
  from '../components/CommercialQueryPanel';

import CustomerQuickContextDrawer
  from '../components/CustomerQuickContextDrawer';

import MarginCenterAttentionCenterDrawer
  from '../components/MarginCenterAttentionCenterDrawer';

import CustomerRelationshipWorkspace
  from '../components/CustomerRelationshipWorkspace';

import MarginCenterTemporalContextBar, {
  buildRecommendedMarginCenterTemporalSearchParams,
  hasExplicitMarginCenterTemporalContext,
} from '../components/MarginCenterTemporalContextBar';

import type {
  MarginCenterCapabilitiesResponse,
  MarginCenterCommercialQueryContext,
  MarginCenterComparisonMode,
  MarginCenterCustomerContextParams,
  MarginCenterExplorerMetric,
  MarginCenterExplorerParams,
  MarginCenterExplorerRow,
  MarginCenterExplorerSignal,
  MarginCenterExplorerSortDirection,
  MarginCenterExplorerSortField,
  MarginCenterFollowUpPortfolioItem,
  MarginCenterFollowUpStatus,
  MarginCenterMeasureCapability,
  MarginCenterOverviewParams,
  MarginCenterOverviewResponse,
  MarginCenterTimeGrain,
} from '../types/marginCenter';

import type {
  MarginCenterCadenceAttentionItem,
} from '../types/cadenceAttention';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';
import PurchaseActivityBlock, { type PurchaseActivityRow } from '../components/cadenceManagement/PurchaseActivityBlock';


const formatNumber = (
  value: number | null | undefined,
  maximumFractionDigits = 2,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  return pricingNumberFormat({
    maximumFractionDigits,
  }).format(value);
};


const formatCurrency = (
  value: number | null | undefined,
  currencyCode: string | null | undefined,
  maximumFractionDigits = 2,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  if (!currencyCode) {
    return formatNumber(
      value,
      maximumFractionDigits,
    );
  }

  try {
    return pricingNumberFormat({
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits:
          maximumFractionDigits === 0
            ? 0
            : Math.min(
              2,
              maximumFractionDigits,
            ),
        maximumFractionDigits,
      },
    ).format(value);
  } catch {
    return formatNumber(
      value,
      maximumFractionDigits,
    );
  }
};


const formatSignedCurrency = (
  value: number | null | undefined,
  currencyCode: string | null | undefined,
  maximumFractionDigits = 2,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  const formattedValue = formatCurrency(
    Math.abs(value),
    currencyCode,
    maximumFractionDigits,
  );

  if (value > 0) {
    return `+${formattedValue}`;
  }

  if (value < 0) {
    return `-${formattedValue}`;
  }

  return formattedValue;
};


const formatPercentage = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  return `${pricingNumberFormat({
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} %`;
};


const formatSignedNumber = (
  value: number | null | undefined,
  maximumFractionDigits = 2,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  const formattedValue =
    pricingNumberFormat({
      maximumFractionDigits,
    }).format(Math.abs(value));

  if (value > 0) {
    return `+${formattedValue}`;
  }

  if (value < 0) {
    return `-${formattedValue}`;
  }

  return formattedValue;
};


const formatSignedPercentage = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return 'No calculable';
  }

  const formattedValue =
    pricingNumberFormat({
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(Math.abs(value));

  if (value > 0) {
    return `+${formattedValue} %`;
  }

  if (value < 0) {
    return `-${formattedValue} %`;
  }

  return `${formattedValue} %`;
};


const formatIsoDate = (
  value: string | null | undefined,
): string => {
  if (!value) {
    return 'Sin fecha';
  }

  const [year, month, day] = value.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
};


const formatShortIsoDate = (
  value: string | null | undefined,
): string => {
  if (!value) {
    return '—';
  }

  const [year, month, day] =
    value.slice(0, 10).split('-').map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
    },
  ).format(
    new Date(year, month - 1, day),
  );
};


const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}$/;


const isIsoDate = (
  value: string | null,
): value is string => {
  if (!value || !ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  const parsedDate =
    new Date(`${value}T00:00:00Z`);

  return (
    !Number.isNaN(parsedDate.getTime())
    && parsedDate.toISOString().slice(0, 10)
      === value
  );
};


const isComparisonMode = (
  value: string | null,
): value is MarginCenterComparisonMode => {
  return (
    value === 'previous_period'
    || value === 'year_over_year'
    || value === 'custom'
    || value === 'none'
  );
};


const isTimeGrain = (
  value: string | null,
): value is MarginCenterTimeGrain => {
  return (
    value === 'day'
    || value === 'week'
    || value === 'month'
  );
};


const getFollowUpStatusLabel = (
  value: MarginCenterFollowUpStatus,
): string => {
  switch (value) {
    case 'new':
      return 'Nuevo';

    case 'in_review':
      return 'En revisión';

    case 'closed':
      return 'Cerrado';
  }
};


type ComparisonDirection =
  | 'positive'
  | 'negative'
  | 'neutral';


const getComparisonDirection = (
  value: number | null | undefined,
): ComparisonDirection => {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return 'neutral';
  }

  return value > 0
    ? 'positive'
    : 'negative';
};


const getComparisonModeLabel = (
  value: MarginCenterComparisonMode,
): string => {
  switch (value) {
    case 'previous_period':
      return 'periodo anterior';

    case 'year_over_year':
      return 'mismo periodo año anterior';

    case 'custom':
      return 'comparación personalizada';

    case 'none':
      return 'sin comparación';
  }
};


type CustomerChangeCriterion =
  | 'revenue_change'
  | 'margin_change'
  | 'negative_margin'
  | 'inactive';


interface CustomerChangeOption {
  value: CustomerChangeCriterion;
  label: string;
  needsComparison: boolean;

  metric: MarginCenterExplorerMetric;

  signal?: MarginCenterExplorerSignal;

  sortField: MarginCenterExplorerSortField;

  defaultSortDirection:
    MarginCenterExplorerSortDirection;

  toggleDirection: boolean;
}


const CUSTOMER_CHANGE_OPTIONS:
  CustomerChangeOption[] = [
    {
      value: 'revenue_change',
      label: 'Ingresos',
      needsComparison: true,
      metric: 'revenue',
      sortField: 'revenue_delta_pct',
      defaultSortDirection: 'desc',
      toggleDirection: true,
    },
    {
      value: 'margin_change',
      label: 'Margen %',
      needsComparison: true,
      metric: 'margin_percentage',
      sortField:
        'margin_percentage_delta_points',
      defaultSortDirection: 'desc',
      toggleDirection: true,
    },
    {
      value: 'negative_margin',
      label: 'Margen negativo',
      needsComparison: false,
      metric: 'margin_amount',
      signal: 'negative_margin',
      sortField: 'margin_amount',
      defaultSortDirection: 'asc',
      toggleDirection: false,
    },
    {
      value: 'inactive',
      label: 'Sin actividad',
      needsComparison: true,
      metric: 'revenue',
      signal: 'inactive',
      sortField: 'entity_label',
      defaultSortDirection: 'asc',
      toggleDirection: false,
    },
  ];


const isCustomerChangeCriterion = (
  value: string | null,
): value is CustomerChangeCriterion => {
  return CUSTOMER_CHANGE_OPTIONS.some(
    (option) => option.value === value,
  );
};


const getOverviewParamsFromSearchParams = (
  searchParams: URLSearchParams,
): MarginCenterOverviewParams => {
  const params: MarginCenterOverviewParams = {};

  const startDate =
    searchParams.get('from');

  const endDate =
    searchParams.get('to');

  const comparisonMode =
    searchParams.get('comparison');

  const comparisonStartDate =
    searchParams.get('comparisonFrom');

  const comparisonEndDate =
    searchParams.get('comparisonTo');

  const timeGrain =
    searchParams.get('grain');

  if (isIsoDate(startDate)) {
    params.start_date = startDate;
  }

  if (isIsoDate(endDate)) {
    params.end_date = endDate;
  }

  if (isComparisonMode(comparisonMode)) {
    params.comparison_mode = comparisonMode;

    if (comparisonMode === 'custom') {
      if (isIsoDate(comparisonStartDate)) {
        params.comparison_start_date =
          comparisonStartDate;
      }

      if (isIsoDate(comparisonEndDate)) {
        params.comparison_end_date =
          comparisonEndDate;
      }
    }
  }

  if (isTimeGrain(timeGrain)) {
    params.time_grain = timeGrain;
  }

  return params;
};


const getMeasureCapability = (
  capabilities: MarginCenterCapabilitiesResponse,
  key: string,
): MarginCenterMeasureCapability | undefined => {
  return capabilities.measures.find(
    (measure) => measure.key === key,
  );
};


const canShowMeasure = (
  capabilities: MarginCenterCapabilitiesResponse,
  key: string,
): boolean => {
  const capability = getMeasureCapability(
    capabilities,
    key,
  );

  return Boolean(
    capability
    && capability.status !== 'unavailable',
  );
};


const OVERVIEW_CHART_METRICS = [
  {
    key: 'revenue',
    label: 'Ingresos',
    capabilityKey: 'revenue',
    seriesField: 'revenue',
    isPercentage: false,
    isCurrency: true,
    maximumFractionDigits: 0,
  },
  {
    key: 'margin_amount',
    label: 'Margen',
    capabilityKey: 'margin_amount',
    seriesField: 'margin',
    isPercentage: false,
    isCurrency: true,
    maximumFractionDigits: 0,
  },
  {
    key: 'margin_percentage',
    label: 'Margen %',
    capabilityKey: 'margin_percentage',
    seriesField: 'margin_pct',
    isPercentage: true,
    isCurrency: false,
    maximumFractionDigits: 1,
  },
  {
    key: 'units',
    label: 'Unidades',
    capabilityKey: 'units',
    seriesField: 'units',
    isPercentage: false,
    isCurrency: false,
    maximumFractionDigits: 2,
  },
  {
    key: 'active_customers',
    label: 'Clientes activos',
    capabilityKey: 'active_customers',
    seriesField: 'active_customers',
    isPercentage: false,
    isCurrency: false,
    maximumFractionDigits: 0,
  },
  {
    key: 'active_products',
    label: 'Productos activos',
    capabilityKey: 'active_products',
    seriesField: 'active_products',
    isPercentage: false,
    isCurrency: false,
    maximumFractionDigits: 0,
  },
] as const;


interface OverviewChartPoint {
  bucket_index: number;

  label: string;

  current_start: string;
  current_end: string;

  comparison_start: string | null;
  comparison_end: string | null;

  current_value: number | null;
  comparison_value: number | null;

  is_partial_bucket: boolean;
}


interface MetricCardProps {
  label: string;
  value: string;
  detail?: string;

  comparisonValue?: string | null;
  comparisonDetail?: string | null;
  comparisonLabel?: string | null;

  comparisonDirection?: ComparisonDirection;
}


const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  detail,
  comparisonValue,
  comparisonDetail,
  comparisonLabel,
  comparisonDirection = 'neutral',
}) => {
  const comparisonColor =
    comparisonDirection === 'positive'
      ? '#34d399'
      : comparisonDirection === 'negative'
        ? '#fb7185'
        : '#94a3b8';

  const directionSymbol =
    comparisonDirection === 'positive'
      ? '↑'
      : comparisonDirection === 'negative'
        ? '↓'
        : '→';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        minHeight: 118,
        bgcolor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h5"
        sx={{
          color: '#f8fafc',
          fontWeight: 800,
          mt: 0.35,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>

      {comparisonValue ? (
        <Box
          sx={{
            mt: 0.75,
            pt: 0.7,
            borderTop:
              '1px solid rgba(148,163,184,0.12)',
          }}
        >
          <Stack
            direction="row"
            spacing={0.55}
            alignItems="baseline"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              variant="body2"
              sx={{
                color: comparisonColor,
                fontWeight: 850,
                lineHeight: 1.2,
              }}
            >
              {directionSymbol}{' '}
              {comparisonValue}
            </Typography>

            {comparisonLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                }}
              >
                vs {comparisonLabel}
              </Typography>
            )}
          </Stack>

          {comparisonDetail && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#64748b',
                mt: 0.15,
                lineHeight: 1.25,
              }}
            >
              {comparisonDetail}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            display: 'block',
            mt: 0.65,
            lineHeight: 1.3,
          }}
        >
          {detail ?? 'Periodo actual'}
        </Typography>
      )}

      {comparisonValue && detail && (
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            display: 'block',
            mt: 0.45,
            lineHeight: 1.25,
          }}
        >
          {detail}
        </Typography>
      )}
    </Paper>
  );
};


// PRC-032: filas visibles de las listas de Overview antes de «Ver todas».
const OVERVIEW_LIST_PREVIEW = 8;

const MarginCenterOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  // PRC-027: Explorer y fichas abiertos desde aquí vuelven a Overview.
  const withOverviewOrigin = (target: string) => withReturnTo(
    target,
    `/margin-center/overview${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
  );

  const [searchParams, setSearchParams] =
    useSearchParams();

  const selectedCustomerId =
    searchParams.get('customer')?.trim()
    || null;

  const selectedRelationshipCustomerId =
    searchParams.get('relationshipCustomer')?.trim()
    || null;

  const selectedRelationshipProductId =
    searchParams.get('relationshipProduct')?.trim()
    || null;

  const selectedRelationshipFollowUpId =
    searchParams.get('relationshipFollowUp')?.trim()
    || null;

  const selectedRelationshipCadenceOccurrence =
    searchParams.get('relationshipCadenceOccurrence')?.trim()
    || null;

  const [
    noticeAnchorEl,
    setNoticeAnchorEl,
  ] = useState<HTMLElement | null>(null);

  const [
    attentionCenterOpen,
    setAttentionCenterOpen,
  ] = useState(false);
  // PRC-032: listas largas sin barra propia: las primeras y «Ver todas».
  const [showAllChanges, setShowAllChanges] = useState(false);

  const [
    lastSuccessfulOverview,
    setLastSuccessfulOverview,
  ] = useState<MarginCenterOverviewResponse | null>(
    null,
  );

  const overviewParams = useMemo(
    () => (
      getOverviewParamsFromSearchParams(
        searchParams,
      )
    ),
    [searchParams],
  );

  const capabilitiesQuery = useQuery({
    queryKey: [
      'margin-center',
      'capabilities',
    ],
    queryFn: getMarginCenterCapabilities,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const hasExplicitTemporalContext =
    hasExplicitMarginCenterTemporalContext(
      searchParams,
    );

  const recommendedTemporalSearchParams =
    useMemo(
      () => (
        buildRecommendedMarginCenterTemporalSearchParams(
          searchParams,
          capabilitiesQuery.data
            ?.dataset
            .last_transaction_date,
        )
      ),
      [
        capabilitiesQuery.data
          ?.dataset
          .last_transaction_date,
        searchParams,
      ],
    );

  const temporalContextReady =
    hasExplicitTemporalContext
    || (
      capabilitiesQuery.isFetched
      && recommendedTemporalSearchParams === null
    );

  useEffect(() => {
    if (!recommendedTemporalSearchParams) {
      return;
    }

    setSearchParams(
      recommendedTemporalSearchParams,
      { replace: true },
    );
  }, [
    recommendedTemporalSearchParams,
    setSearchParams,
  ]);

  const overviewRequest = useQuery({
    queryKey: [
      'margin-center',
      'overview',
      overviewParams,
    ],
    queryFn: () => (
      getMarginCenterOverview(
        overviewParams,
      )
    ),
    enabled: temporalContextReady,
    placeholderData: (previousData) =>
      previousData,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // PRC-019: paso «margen visto» (Overview abierto con datos).
  useMarkOnboardingEvent(
    'overview',
    !!overviewRequest.data
      && overviewRequest.data.status !== 'empty_dataset',
  );

  useEffect(() => {
    if (
      !overviewRequest.data
      || overviewRequest.isPlaceholderData
    ) {
      return;
    }

    setLastSuccessfulOverview(
      overviewRequest.data,
    );
  }, [
    overviewRequest.data,
    overviewRequest.isPlaceholderData,
  ]);

  const overviewQuery = {
    ...overviewRequest,
    data:
      overviewRequest.data
      ?? lastSuccessfulOverview
      ?? undefined,
  };

  const followUpPortfolioQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'portfolio',
      6,
    ],
    queryFn: () =>
      getMarginCenterFollowUpPortfolio({
        limit: 6,
      }),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const followUpAttentionQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'attention',
      'overview',
      'due',
    ],
    queryFn: () =>
      getMarginCenterFollowUpAttention({
        state: 'due',
        horizon_days: 7,
        limit: 1,
      }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const dueFollowUpCount =
    followUpAttentionQuery.data
      ?.summary.due_count
    ?? 0;

  const cadenceAttentionQuery = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'overview',
      5,
    ],
    queryFn: () =>
      getMarginCenterCadenceAttention({
        lifecycle: 'active_attention',
        limit: 1,
      }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const cadenceAttentionCount =
    cadenceAttentionQuery.data
      ?.summary.active_count
    ?? 0;

  const commercialAttentionCount =
    dueFollowUpCount
    + cadenceAttentionCount;

  const attentionUnavailable =
    followUpAttentionQuery.isError
    || cadenceAttentionQuery.isError;

  const attentionPending =
    followUpAttentionQuery.isPending
    || cadenceAttentionQuery.isPending;

  const requestedCustomerCriterion =
    searchParams.get('customerCriterion');

  const requestedCustomerSortDirection:
    MarginCenterExplorerSortDirection =
      searchParams.get('customerDirection')
        === 'asc'
        ? 'asc'
        : 'desc';

  const requestedCustomerChangeOption =
    CUSTOMER_CHANGE_OPTIONS.find(
      (option) =>
        option.value
        === (
          isCustomerChangeCriterion(
            requestedCustomerCriterion,
          )
            ? requestedCustomerCriterion
            : 'revenue_change'
        ),
    )
    ?? CUSTOMER_CHANGE_OPTIONS[0];

  const customerChangeOption = useMemo(() => {
    const overview = overviewQuery.data;

    if (
      !overview
      || overview.status !== 'ok'
    ) {
      return requestedCustomerChangeOption;
    }

    if (
      overview.selection.comparison_mode
        === 'none'
      && requestedCustomerChangeOption
        .needsComparison
    ) {
      return (
        CUSTOMER_CHANGE_OPTIONS.find(
          (option) =>
            option.value === 'negative_margin',
        )
        ?? requestedCustomerChangeOption
      );
    }

    return requestedCustomerChangeOption;
  }, [
    overviewQuery.data,
    requestedCustomerChangeOption,
  ]);

  const customerChangeSortDirection:
    MarginCenterExplorerSortDirection =
      customerChangeOption.toggleDirection
        ? requestedCustomerSortDirection
        : customerChangeOption
          .defaultSortDirection;

  const customerChangesCursor =
    searchParams.get('customerCursor')?.trim()
    || undefined;

  const customerChangesParams =
    useMemo<MarginCenterExplorerParams | null>(
      () => {
        const overview = overviewQuery.data;

        if (
          !overview
          || overview.status !== 'ok'
        ) {
          return null;
        }

        return {
          start_date:
            overview.selection.start_date
            ?? undefined,

          end_date:
            overview.selection.end_date
            ?? undefined,

          comparison_mode:
            overview.selection.comparison_mode,

          comparison_start_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_start_date
                ?? undefined
              )
              : undefined,

          comparison_end_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_end_date
                ?? undefined
              )
              : undefined,

          time_grain:
            overview.selection.time_grain,

          dimension: 'customer',
          metric: customerChangeOption.metric,

          signal:
            customerChangeOption.signal,

          sort_field:
            customerChangeOption.sortField,

          sort_direction:
            customerChangeSortDirection,

          limit: 50,
          cursor: customerChangesCursor,
        };
      },
      [
        customerChangeOption,
        customerChangeSortDirection,
        customerChangesCursor,
        overviewQuery.data,
      ],
    );

  const customerChangesQuery = useQuery({
    queryKey: [
      'margin-center',
      'overview',
      'customer-changes',
      customerChangesParams,
    ],
    queryFn: () => {
      if (!customerChangesParams) {
        throw new Error(
          'Customer change parameters are unavailable.',
        );
      }

      return getMarginCenterExplorer(
        customerChangesParams,
      );
    },
    enabled: Boolean(customerChangesParams),
    placeholderData: (previousData) =>
      previousData,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const commercialQueryContext =
    useMemo<
      MarginCenterCommercialQueryContext | null
    >(
      () => {
        const overview = overviewQuery.data;

        if (
          !overview
          || overview.status !== 'ok'
        ) {
          return null;
        }

        return {
          start_date:
            overview.selection.start_date
            ?? undefined,

          end_date:
            overview.selection.end_date
            ?? undefined,

          comparison_mode:
            overview.selection.comparison_mode,

          comparison_start_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_start_date
                ?? undefined
              )
              : undefined,

          comparison_end_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_end_date
                ?? undefined
              )
              : undefined,

          time_grain:
            overview.selection.time_grain,
        };
      },
      [overviewQuery.data],
    );

  const customerContextParams =
    useMemo<MarginCenterCustomerContextParams>(
      () => {
        const overview = overviewQuery.data;

        if (
          !overview
          || overview.status !== 'ok'
        ) {
          return {};
        }

        return {
          start_date:
            overview.selection.start_date
            ?? undefined,

          end_date:
            overview.selection.end_date
            ?? undefined,

          comparison_mode:
            overview.selection.comparison_mode,

          comparison_start_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_start_date
                ?? undefined
              )
              : undefined,

          comparison_end_date:
            overview.selection.comparison_mode
              === 'custom'
              ? (
                overview.selection
                  .comparison_end_date
                ?? undefined
              )
              : undefined,

          time_grain:
            overview.selection.time_grain,
        };
      },
      [overviewQuery.data],
    );

  const isInitialLoading =
    capabilitiesQuery.isLoading
    || (
      overviewQuery.isLoading
      && !overviewQuery.data
    );

  const isOverviewRefreshing =
    overviewQuery.isFetching
    && Boolean(overviewQuery.data);

  const hasOverviewRefreshError =
    overviewQuery.isError
    && Boolean(overviewQuery.data);

  const currencyCode =
    capabilitiesQuery.data
      ?.presentation
      .currency_code
    ?? null;

  const metricCards = useMemo(() => {
    const capabilities = capabilitiesQuery.data;
    const overview = overviewQuery.data;

    if (
      !capabilities
      || !overview
      || overview.status !== 'ok'
    ) {
      return [];
    }

    const current = overview.current;
    const changes = overview.changes;

    const hasResolvedCosts =
      current.resolved_cost_rows > 0;

    const comparisonLabel =
      overview.selection.comparison_mode === 'none'
        ? null
        : getComparisonModeLabel(
          overview.selection.comparison_mode,
        );

    const cards = [
      {
        key: 'revenue',
        label: 'Ingresos',
        value: formatCurrency(
          current.revenue,
          currencyCode,
        ),
        detail: 'Importe observado en el periodo',

        comparisonValue:
          changes?.revenue_delta_pct !== null
          && changes?.revenue_delta_pct !== undefined
            ? formatSignedPercentage(
              changes.revenue_delta_pct,
            )
            : changes?.revenue_delta !== null
              && changes?.revenue_delta !== undefined
              ? formatSignedCurrency(
                changes.revenue_delta,
                currencyCode,
              )
              : null,

        comparisonDetail:
          changes?.revenue_delta !== null
          && changes?.revenue_delta !== undefined
            ? `Δ absoluto ${
              formatSignedCurrency(
                changes.revenue_delta,
                currencyCode,
              )
            }`
            : null,

        comparisonLabel,

        comparisonDirection:
          getComparisonDirection(
            changes?.revenue_delta_pct
            ?? changes?.revenue_delta,
          ),
      },
      {
        key: 'margin_amount',
        label: 'Margen',
        value: hasResolvedCosts
          ? formatCurrency(
            current.margin,
            currencyCode,
          )
          : 'No calculable',
        detail: hasResolvedCosts
          ? (
            `Ingresos con coste resuelto: ${
              formatPercentage(
                current.resolved_revenue_coverage_pct,
              )
            }`
          )
          : 'Sin operaciones con coste resuelto',

        comparisonValue:
          hasResolvedCosts
          && changes?.margin_delta_pct !== null
          && changes?.margin_delta_pct !== undefined
            ? formatSignedPercentage(
              changes.margin_delta_pct,
            )
            : hasResolvedCosts
              && changes?.margin_delta !== null
              && changes?.margin_delta !== undefined
              ? formatSignedCurrency(
                changes.margin_delta,
                currencyCode,
              )
              : null,

        comparisonDetail:
          hasResolvedCosts
          && changes?.margin_delta !== null
          && changes?.margin_delta !== undefined
            ? `Δ absoluto ${
              formatSignedCurrency(
                changes.margin_delta,
                currencyCode,
              )
            }`
            : null,

        comparisonLabel,

        comparisonDirection:
          getComparisonDirection(
            changes?.margin_delta_pct
            ?? changes?.margin_delta,
          ),
      },
      {
        key: 'margin_percentage',
        label: 'Margen %',
        value: hasResolvedCosts
          ? formatPercentage(
            current.margin_pct,
          )
          : 'No calculable',
        detail: hasResolvedCosts
          ? 'Calculado sobre operaciones con coste resuelto'
          : 'Sin operaciones con coste resuelto',

        comparisonValue:
          hasResolvedCosts
          && changes
            ?.margin_delta_percentage_points !== null
          && changes
            ?.margin_delta_percentage_points !== undefined
            ? `${
              formatSignedNumber(
                changes
                  .margin_delta_percentage_points,
                1,
              )
            } pp`
            : null,

        comparisonDetail: null,
        comparisonLabel,

        comparisonDirection:
          getComparisonDirection(
            changes
              ?.margin_delta_percentage_points,
          ),
      },
      {
        key: 'active_customers',
        label: 'Clientes activos',
        value: formatNumber(
          current.active_customers,
          0,
        ),
        detail: 'Clientes con actividad en el periodo',

        comparisonValue:
          changes?.active_customers_delta !== null
          && changes?.active_customers_delta
            !== undefined
            ? formatSignedNumber(
              changes.active_customers_delta,
              0,
            )
            : null,

        comparisonDetail: null,
        comparisonLabel,

        comparisonDirection:
          getComparisonDirection(
            changes?.active_customers_delta,
          ),
      },
      {
        key: 'active_products',
        label: 'Productos activos',
        value: formatNumber(
          current.active_products,
          0,
        ),
        detail: 'Productos con actividad en el periodo',

        comparisonValue:
          changes?.active_products_delta !== null
          && changes?.active_products_delta
            !== undefined
            ? formatSignedNumber(
              changes.active_products_delta,
              0,
            )
            : null,

        comparisonDetail: null,
        comparisonLabel,

        comparisonDirection:
          getComparisonDirection(
            changes?.active_products_delta,
          ),
      },
    ];

    return cards.filter(
      (card) => canShowMeasure(
        capabilities,
        card.key,
      ),
    );
  }, [
    capabilitiesQuery.data,
    currencyCode,
    overviewQuery.data,
  ]);

  const availableChartMetrics = useMemo(() => {
    const capabilities = capabilitiesQuery.data;

    if (!capabilities) {
      return [];
    }

    return OVERVIEW_CHART_METRICS.filter(
      (metric) => canShowMeasure(
        capabilities,
        metric.capabilityKey,
      ),
    );
  }, [capabilitiesQuery.data]);

  const selectedChartMetric = useMemo(() => {
    const requestedMetric =
      searchParams.get('metric');

    return (
      availableChartMetrics.find(
        (metric) =>
          metric.key === requestedMetric,
      )
      ?? availableChartMetrics.find(
        (metric) => metric.key === 'revenue',
      )
      ?? availableChartMetrics[0]
      ?? null
    );
  }, [
    availableChartMetrics,
    searchParams,
  ]);

  const formatSelectedChartValue = (
    value: number | null | undefined,
  ): string => {
    if (!selectedChartMetric) {
      return formatNumber(value);
    }

    if (selectedChartMetric.isPercentage) {
      return formatPercentage(value);
    }

    if (selectedChartMetric.isCurrency) {
      return formatCurrency(
        value,
        currencyCode,
        selectedChartMetric.maximumFractionDigits,
      );
    }

    return formatNumber(
      value,
      selectedChartMetric.maximumFractionDigits,
    );
  };

  const chartSeriesData =
    useMemo<OverviewChartPoint[]>(() => {
      const overview = overviewQuery.data;

      if (
        !overview
        || overview.status !== 'ok'
        || !selectedChartMetric
      ) {
        return [];
      }

      const currentSeries = overview.series
        .filter(
          (point) => point.period === 'current',
        )
        .sort(
          (left, right) =>
            left.bucket_index - right.bucket_index,
        );

      const comparisonSeries = overview.series
        .filter(
          (point) =>
            point.period === 'comparison',
        );

      const comparisonByBucket = new Map(
        comparisonSeries.map(
          (point) => [
            point.bucket_index,
            point,
          ] as const,
        ),
      );

      return currentSeries.map((point) => {
        const comparisonPoint =
          comparisonByBucket.get(
            point.bucket_index,
          );

        return {
          bucket_index:
            point.bucket_index,

          label:
            formatShortIsoDate(
              point.covered_start,
            ),

          current_start:
            point.covered_start,

          current_end:
            point.covered_end,

          comparison_start:
            comparisonPoint
              ?.covered_start
            ?? null,

          comparison_end:
            comparisonPoint
              ?.covered_end
            ?? null,

          current_value:
            point[
              selectedChartMetric.seriesField
            ],

          comparison_value:
            comparisonPoint
              ? comparisonPoint[
                selectedChartMetric
                  .seriesField
              ]
              : null,

          is_partial_bucket:
            point.is_partial_bucket,
        };
      });
    }, [
      overviewQuery.data,
      selectedChartMetric,
    ]);

  const observedSituations = useMemo(() => {
    const overview = overviewQuery.data;

    if (!overview || overview.status !== 'ok') {
      return [];
    }

    const signals = overview.signals;

    const situations: {
      key: string;
      label: string;
      count: number;
      value: string;
      detail: string;
      scope: string;
      quickFacts: Array<{
        label: string;
        value: string;
      }>;
      known: string[];
      notKnown: string[];
    }[] = [];

    if (
      signals.destruction_of_margin
      && signals.destruction_of_margin
        .transaction_count > 0
    ) {
      situations.push({
        key: 'destruction-of-margin',
        label: 'Operaciones con margen negativo',
        count: signals.destruction_of_margin.transaction_count,
        value: formatNumber(
          signals.destruction_of_margin
            .transaction_count,
          0,
        ),
        detail:
          'Operaciones del periodo cuyo margen observado es negativo.',
        scope: 'Operaciones',
        quickFacts: [
          {
            label: 'Margen negativo acumulado',
            value: formatCurrency(
              signals.destruction_of_margin
                .negative_margin_amount,
              currencyCode,
            ),
          },
          {
            label: 'Ingresos afectados',
            value: formatCurrency(
              signals.destruction_of_margin
                .affected_revenue,
              currencyCode,
            ),
          },
        ],
        known: [
          `${
            formatNumber(
              signals.destruction_of_margin
                .transaction_count,
              0,
            )
          } operaciones forman parte de esta señal en el periodo aplicado.`,
          `El margen acumulado observado en esas operaciones es ${
            formatNumber(
              signals.destruction_of_margin
                .negative_margin_amount,
            )
          }.`,
          `Los ingresos afectados suman ${
            formatCurrency(
              signals.destruction_of_margin
                .affected_revenue,
              currencyCode,
            )
          }.`,
        ],
        notKnown: [
          'La respuesta agregada del Overview no identifica las transacciones concretas incluidas.',
          'La señal no demuestra por qué se produjo el margen negativo ni qué acción comercial corresponde.',
        ],
      });
    }

    if (
      typeof signals.customers_with_negative_margin
      === 'number'
      && signals.customers_with_negative_margin > 0
    ) {
      situations.push({
        key: 'customers-negative-margin',
        label: 'Clientes con margen negativo',
        count: signals.customers_with_negative_margin,
        value: formatNumber(
          signals.customers_with_negative_margin,
          0,
        ),
        detail:
          'Clientes cuyo margen agregado del periodo es negativo.',
        scope: 'Clientes',
        quickFacts: [
          {
            label: 'Sobre clientes activos',
            value: overview.current.active_customers > 0
              ? `${
                formatNumber(
                  signals.customers_with_negative_margin,
                  0,
                )
              } de ${
                formatNumber(
                  overview.current.active_customers,
                  0,
                )
              } · ${
                formatPercentage(
                  (
                    signals.customers_with_negative_margin
                    / overview.current.active_customers
                  ) * 100,
                )
              }`
              : 'Sin base activa calculable',
          },
        ],
        known: [
          `El contrato identifica ${
            formatNumber(
              signals.customers_with_negative_margin,
              0,
            )
          } clientes con margen negativo en el periodo aplicado.`,
        ],
        notKnown: [
          'La respuesta agregada del Overview no devuelve las identidades de esos clientes.',
          'La señal no determina la causa comercial ni implica por sí sola una recomendación.',
        ],
      });
    }

    if (
      typeof signals.customers_with_margin_erosion
      === 'number'
      && signals.customers_with_margin_erosion > 0
    ) {
      situations.push({
        key: 'customers-margin-erosion',
        label: 'Clientes con menor margen %',
        count: signals.customers_with_margin_erosion,
        value: formatNumber(
          signals.customers_with_margin_erosion,
          0,
        ),
        detail:
          'Clientes cuyo margen % actual es inferior al periodo comparado.',
        scope: 'Clientes',
        quickFacts: [
          {
            label: 'Sobre clientes activos',
            value: overview.current.active_customers > 0
              ? `${
                formatNumber(
                  signals.customers_with_margin_erosion,
                  0,
                )
              } de ${
                formatNumber(
                  overview.current.active_customers,
                  0,
                )
              } · ${
                formatPercentage(
                  (
                    signals.customers_with_margin_erosion
                    / overview.current.active_customers
                  ) * 100,
                )
              }`
              : 'Sin base activa calculable',
          },
        ],
        known: [
          `${
            formatNumber(
              signals.customers_with_margin_erosion,
              0,
            )
          } clientes tienen un margen % menor que en el periodo comparado.`,  
        ],
        notKnown: [
          'El Overview agregado no devuelve aquí las identidades ni la variación individual del margen % por cliente.',
          'El dato observado no permite atribuir el cambio a una causa comercial concreta.',
        ],
      });
    }

    if (
      typeof signals
        .customers_with_revenue_contraction
      === 'number'
      && signals
        .customers_with_revenue_contraction > 0
    ) {
      situations.push({
        key: 'customers-revenue-contraction',
        label: 'Clientes con menos ingresos',
        count:
          signals.customers_with_revenue_contraction,
        value: formatNumber(
          signals
            .customers_with_revenue_contraction,
          0,
        ),
        detail:
          'Tienen ingresos en ambos periodos, pero facturan menos en el actual.',
        scope: 'Clientes',
        quickFacts: [
          {
            label: 'Sobre clientes activos',
            value: overview.current.active_customers > 0
              ? `${
                formatNumber(
                  signals.customers_with_revenue_contraction,
                  0,
                )
              } de ${
                formatNumber(
                  overview.current.active_customers,
                  0,
                )
              } · ${
                formatPercentage(
                  (
                    signals.customers_with_revenue_contraction
                    / overview.current.active_customers
                  ) * 100,
                )
              }`
              : 'Sin base activa calculable',
          },
        ],
        known: [
          `${
            formatNumber(
              signals
                .customers_with_revenue_contraction,
              0,
            )
          } clientes tienen menos ingresos que en el periodo comparado.`,
        ],
        notKnown: [
          'El Overview agregado no devuelve aquí las identidades ni la variación individual de ingresos por cliente.',
          'El dato observado no permite atribuir la reducción a una causa comercial concreta.',
        ],
      });
    }

    if (
      typeof signals.inactive_customers
      === 'number'
      && signals.inactive_customers > 0
    ) {
      situations.push({
        key: 'inactive-customers',
        label: 'Clientes sin actividad',
        count: signals.inactive_customers,
        value: formatNumber(
          signals.inactive_customers,
          0,
        ),
        detail:
          'Tenían ingresos en el periodo comparado y registran cero en el actual.',
        scope: 'Clientes',
        quickFacts: [
          {
            label: 'Sobre clientes activos en comparación',
            value: (
              overview.comparison
              && overview.comparison.active_customers > 0
            )
              ? `${
                formatNumber(
                  signals.inactive_customers,
                  0,
                )
              } de ${
                formatNumber(
                  overview.comparison.active_customers,
                  0,
                )
              } · ${
                formatPercentage(
                  (
                    signals.inactive_customers
                    / overview.comparison.active_customers
                  ) * 100,
                )
              }`
              : 'Sin base comparativa calculable',
          },
        ],
        known: [
          `${
            formatNumber(
              signals.inactive_customers,
              0,
            )
          } clientes han sido identificados como inactivos por el contrato actual.`,
        ],
        notKnown: [
          'La respuesta agregada del Overview no expone aquí las identidades ni su evidencia transaccional individual.',
          'La señal no determina por sí sola si la inactividad requiere una acción comercial.',
        ],
      });
    }

    return situations;
  }, [
    currencyCode,
    overviewQuery.data,
  ]);

  const noticesOpen =
    Boolean(noticeAnchorEl);

  // La comparación ejecutiva vive ahora dentro
  // de las propias tarjetas KPI.

  const priceVolumeCostMetrics = useMemo(() => {
    const capabilities = capabilitiesQuery.data;
    const overview = overviewQuery.data;

    if (
      !capabilities
      || !overview
      || overview.status !== 'ok'
    ) {
      return [];
    }

    const current = overview.current;
    const comparison = overview.comparison;

    const metrics = [
      {
        key: 'average_net_unit_price',
        label: 'Precio neto medio',
        currentValue: formatCurrency(
          current.average_net_unit_price,
          currencyCode,
        ),
        comparisonValue:
          comparison
            ? formatCurrency(
              comparison.average_net_unit_price,
              currencyCode,
            )
            : null,
        comparisonChange: null,
      },
      {
        key: 'units',
        label: 'Volumen',
        currentValue: formatNumber(
          current.units,
        ),
        comparisonValue:
          comparison
            ? formatNumber(
              comparison.units,
            )
            : null,
        comparisonChange:
          overview.changes
            ?.units_delta_pct
          ?? null,
      },
      {
        key: 'average_unit_cost',
        label: 'Coste unitario medio',
        currentValue: formatCurrency(
          current.average_unit_cost,
          currencyCode,
        ),
        comparisonValue:
          comparison
            ? formatCurrency(
              comparison.average_unit_cost,
              currencyCode,
            )
            : null,
        comparisonChange: null,
      },
    ];

    return metrics.filter(
      (metric) => canShowMeasure(
        capabilities,
        metric.key,
      ),
    );
  }, [
    capabilitiesQuery.data,
    currencyCode,
    overviewQuery.data,
  ]);

  const openRelationship = (
    customerId: string,
    productId: string,
  ) => {
    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.set(
      'relationshipCustomer',
      customerId,
    );

    nextSearchParams.set(
      'relationshipProduct',
      productId,
    );

    nextSearchParams.delete('relationshipFollowUp');
    nextSearchParams.delete('relationshipCadenceOccurrence');
    nextSearchParams.delete('relationshipReturnTo');
    setSearchParams(nextSearchParams);
  };

  // PRC-048: lupa de «Actividad de compra»: abre la relación con su episodio
  // (pendiente o histórico), con su seguimiento (en seguimiento) o sin más.
  const openPurchaseActivityRow = (row: PurchaseActivityRow) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('relationshipCustomer', row.customer_id);
    nextSearchParams.set('relationshipProduct', row.product_id);
    nextSearchParams.delete('relationshipFollowUp');
    nextSearchParams.delete('relationshipCadenceOccurrence');
    if (row.follow_up_id) {
      nextSearchParams.set('relationshipFollowUp', row.follow_up_id);
    } else if (row.tab === 'pending' || row.tab === 'historical') {
      nextSearchParams.set('relationshipCadenceOccurrence', row.first_outside_p90_date);
    }
    nextSearchParams.delete('relationshipReturnTo');
    setSearchParams(nextSearchParams);
  };

  const openCadenceAttentionRelationship = (
    item: MarginCenterCadenceAttentionItem,
  ) => {
    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.set(
      'relationshipCustomer',
      item.customer_id,
    );

    nextSearchParams.set(
      'relationshipProduct',
      item.product_id,
    );

    nextSearchParams.set(
      'relationshipCadenceOccurrence',
      item.first_outside_p90_date,
    );

    nextSearchParams.delete('relationshipFollowUp');
    nextSearchParams.delete('relationshipReturnTo');

    setSearchParams(nextSearchParams);
  };

  const openFollowUpRelationship = (
    item: MarginCenterFollowUpPortfolioItem,
  ) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('relationshipCustomer', item.customer_id);
    nextSearchParams.set('relationshipProduct', item.product_id);
    nextSearchParams.set('relationshipFollowUp', item.id);
    setSearchParams(nextSearchParams);
  };

  const selectRelationshipFollowUp = (id: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('relationshipFollowUp', id);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const closeCadenceRelationship = () => {
    const relationshipReturnTo =
      searchParams.get('relationshipReturnTo')?.trim();

    if (
      relationshipReturnTo
      && relationshipReturnTo.startsWith('/margin-center/')
      && !relationshipReturnTo.startsWith('//')
    ) {
      navigate(relationshipReturnTo);
      return;
    }

    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.delete(
      'relationshipCustomer',
    );

    nextSearchParams.delete(
      'relationshipProduct',
    );

    nextSearchParams.delete('relationshipFollowUp');
    nextSearchParams.delete('relationshipCadenceOccurrence');
    nextSearchParams.delete('relationshipReturnTo');

    setSearchParams(
      nextSearchParams,
      {
        replace: true,
      },
    );
  };

  const openCustomerContext = (
    customerId: string,
  ) => {
    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.set(
      'customer',
      customerId,
    );

    setSearchParams(nextSearchParams);
  };

  const closeCustomerContext = () => {
    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.delete('customer');

    setSearchParams(
      nextSearchParams,
      {
        replace: true,
      },
    );
  };

  const openFullCustomerContext = () => {
    if (!selectedCustomerId) {
      return;
    }

    const nextSearchParams =
      new URLSearchParams(searchParams);

    nextSearchParams.delete('customer');
    nextSearchParams.delete(
      'customerCriterion',
    );
    nextSearchParams.delete(
      'customerDirection',
    );

    // Compatibilidad con URLs generadas
    // durante la iteración anterior.
    nextSearchParams.delete(
      'customerSignal',
    );

    const query =
      nextSearchParams.toString();

    navigate(withOverviewOrigin(
      `/margin-center/customers/${
        encodeURIComponent(
          selectedCustomerId,
        )
      }${query ? `?${query}` : ''}`,
    ));
  };

  const openCustomerChangesInExplorer = () => {
    const overview = overviewQuery.data;

    if (
      !overview
      || overview.status !== 'ok'
    ) {
      return;
    }

    const nextSearchParams =
      new URLSearchParams();

    if (overview.selection.start_date) {
      nextSearchParams.set(
        'from',
        overview.selection.start_date,
      );
    }

    if (overview.selection.end_date) {
      nextSearchParams.set(
        'to',
        overview.selection.end_date,
      );
    }

    nextSearchParams.set(
      'comparison',
      overview.selection.comparison_mode,
    );

    if (
      overview.selection.comparison_mode
        === 'custom'
      && overview.selection
        .comparison_start_date
    ) {
      nextSearchParams.set(
        'comparisonFrom',
        overview.selection
          .comparison_start_date,
      );
    }

    if (
      overview.selection.comparison_mode
        === 'custom'
      && overview.selection
        .comparison_end_date
    ) {
      nextSearchParams.set(
        'comparisonTo',
        overview.selection
          .comparison_end_date,
      );
    }

    nextSearchParams.set(
      'grain',
      overview.selection.time_grain,
    );

    nextSearchParams.set(
      'dimension',
      'customer',
    );

    nextSearchParams.set(
      'metric',
      customerChangeOption.metric,
    );

    if (customerChangeOption.signal) {
      nextSearchParams.set(
        'signal',
        customerChangeOption.signal,
      );
    }

    nextSearchParams.set(
      'sort',
      customerChangeOption.sortField,
    );

    nextSearchParams.set(
      'direction',
      customerChangeSortDirection,
    );

    nextSearchParams.set(
      'limit',
      '50',
    );

    navigate(withOverviewOrigin(
      `/margin-center/explorer?${
        nextSearchParams.toString()
      }`,
    ));
  };

  // PRC-031: la estructura de Overview aparece al instante; los bloques, en gris.
  if (isInitialLoading) {
    return (
      <PageSkeleton
        eyebrow="Centro de decisión"
        title="Overview"
        variant="dashboard"
      />
    );
  }

  if (capabilitiesQuery.isError) {
    return (
      <Container
        maxWidth="xl"
        sx={{ py: 4 }}
      >
        <Alert severity="error">
          No se han podido resolver las capacidades
          analíticas de esta cuenta. No es seguro
          interpretar el Overview sin este contrato.
        </Alert>
      </Container>
    );
  }

  if (
    overviewQuery.isError
    && !overviewQuery.data
  ) {
    return (
      <Container
        maxWidth="xl"
        sx={{ py: 4 }}
      >
        <Alert severity="error">
          No se ha podido consultar el Overview del
          Centro de Decisión. La configuración de la
          cuenta permanece disponible, pero los datos
          del periodo no se han cargado.
        </Alert>
      </Container>
    );
  }

  const capabilities = capabilitiesQuery.data;
  const overview = overviewQuery.data;

  if (!capabilities || !overview) {
    return (
      <Container
        maxWidth="xl"
        sx={{ py: 4 }}
      >
        <Alert severity="error">
          La respuesta del Centro de Decisión está
          incompleta.
        </Alert>
      </Container>
    );
  }

  if (overview.status === 'empty_dataset') {
    return (
      <Container
        maxWidth="xl"
        sx={{ py: 4 }}
      >
        <Stack spacing={3}>
          <PageHeader
            eyebrow="Centro de decisión"
            title="Overview"
            back="origin"
            sx={{ mb: 0 }}
          />

          {/* PRC-019 · B2: estado vacío con acceso a la carga. */}
          <OverviewEmptyState />
        </Stack>
      </Container>
    );
  }

  const current = overview.current;

  const openExplorer = () => {
    const nextSearchParams =
      new URLSearchParams();

    if (overview.selection.start_date) {
      nextSearchParams.set(
        'from',
        overview.selection.start_date,
      );
    }

    if (overview.selection.end_date) {
      nextSearchParams.set(
        'to',
        overview.selection.end_date,
      );
    }

    nextSearchParams.set(
      'comparison',
      overview.selection.comparison_mode,
    );

    if (
      overview.selection.comparison_mode === 'custom'
      && overview.selection.comparison_start_date
    ) {
      nextSearchParams.set(
        'comparisonFrom',
        overview.selection.comparison_start_date,
      );
    }

    if (
      overview.selection.comparison_mode === 'custom'
      && overview.selection.comparison_end_date
    ) {
      nextSearchParams.set(
        'comparisonTo',
        overview.selection.comparison_end_date,
      );
    }

    nextSearchParams.set(
      'grain',
      overview.selection.time_grain,
    );

    const query =
      nextSearchParams.toString();

    navigate(withOverviewOrigin(
      `/margin-center/explorer${
        query ? `?${query}` : ''
      }`,
    ));
  };

  // O-12C: Situaciones navegan directamente a la investigación.
  // O-12E: una situación transaccional no navega a una entidad distinta.
  const openSituationInExplorer = (
    situation: (typeof observedSituations)[number],
  ) => {
    const investigation = (
      situation.key === 'customers-negative-margin'
        ? {
          signal: 'negative_margin',
          metric: 'margin_amount',
          sort: 'margin_amount',
          direction: 'asc',
        }
        : situation.key === 'customers-margin-erosion'
          ? {
            signal: 'margin_erosion',
            metric: 'margin_percentage',
            sort: 'margin_percentage_delta_points',
            direction: 'asc',
          }
          : situation.key === 'customers-revenue-contraction'
            ? {
              signal: 'revenue_contraction',
              metric: 'revenue',
              sort: 'revenue_delta',
              direction: 'asc',
            }
            : situation.key === 'inactive-customers'
              ? {
                signal: 'inactive',
                metric: 'revenue',
                sort: 'revenue_delta',
                direction: 'asc',
              }
              : null
    );

    if (!investigation) {
      return;
    }

    const nextSearchParams = new URLSearchParams();

    if (overview.selection.start_date) {
      nextSearchParams.set('from', overview.selection.start_date);
    }

    if (overview.selection.end_date) {
      nextSearchParams.set('to', overview.selection.end_date);
    }

    nextSearchParams.set(
      'comparison',
      overview.selection.comparison_mode,
    );

    if (
      overview.selection.comparison_mode === 'custom'
      && overview.selection.comparison_start_date
    ) {
      nextSearchParams.set(
        'comparisonFrom',
        overview.selection.comparison_start_date,
      );
    }

    if (
      overview.selection.comparison_mode === 'custom'
      && overview.selection.comparison_end_date
    ) {
      nextSearchParams.set(
        'comparisonTo',
        overview.selection.comparison_end_date,
      );
    }

    nextSearchParams.set(
      'grain',
      overview.selection.time_grain,
    );
    nextSearchParams.set('dimension', 'customer');
    nextSearchParams.set('metric', investigation.metric);
    nextSearchParams.set('signal', investigation.signal);
    nextSearchParams.set('sort', investigation.sort);
    nextSearchParams.set('direction', investigation.direction);
    nextSearchParams.set('limit', '50');
    nextSearchParams.set('origin', 'overview-situation');
    nextSearchParams.set('originSituation', situation.key);

    navigate(withOverviewOrigin(
      `/margin-center/explorer?${nextSearchParams.toString()}`,
    ));
  };

  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={2}>
        {/* PRC-032: cabecera común; «Volver» solo si se llegó con origen (PRC-027). */}
        <PageHeader
          eyebrow="Centro de decisión"
          title="Overview"
          description="Qué está ocurriendo en el negocio, dónde merece la pena investigar y su evidencia."
          back="origin"
          sx={{ mb: 0 }}
          actions={(
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                flexShrink: 0,
                pt: {
                  xs: 0,
                  md: 0.25,
                },
              }}
            >
              <Button
                variant="outlined"
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                onClick={() => {
                  const next = new URLSearchParams({
                    context: 'global',
                    returnTo:
                      `/margin-center/overview${
                        searchParams.toString()
                          ? `?${searchParams.toString()}`
                          : ''
                      }`,
                  });

                  if (overview.selection.start_date) {
                    next.set(
                      'from',
                      overview.selection.start_date,
                    );
                  }

                  if (overview.selection.end_date) {
                    next.set(
                      'to',
                      overview.selection.end_date,
                    );
                  }

                  next.set(
                    'comparison',
                    overview.selection.comparison_mode,
                  );

                  if (
                    overview.selection.comparison_mode
                      === 'custom'
                    && overview.selection
                      .comparison_start_date
                  ) {
                    next.set(
                      'comparisonFrom',
                      overview.selection
                        .comparison_start_date,
                    );
                  }

                  if (
                    overview.selection.comparison_mode
                      === 'custom'
                    && overview.selection
                      .comparison_end_date
                  ) {
                    next.set(
                      'comparisonTo',
                      overview.selection
                        .comparison_end_date,
                    );
                  }

                  next.set(
                    'grain',
                    overview.selection.time_grain,
                  );

                  navigate(
                    `/margin-center/control-comercial?${
                      next.toString()
                    }`,
                  );
                }}
              >
                Control comercial
              </Button>
              <Button
                variant="contained"
                startIcon={
                  <SearchRoundedIcon fontSize="small" />
                }
                onClick={openExplorer}
                sx={{
                  minHeight: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: '#38bdf8',
                  color: '#0f172a',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#7dd3fc',
                    boxShadow: 'none',
                  },
                }}
              >
                Explorar
              </Button>
            </Stack>
          )}
        />


        {isOverviewRefreshing && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border:
                '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              overflow: 'hidden',
            }}
          >
            <LinearProgress />

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 2,
                py: 1.25,
                color: '#94a3b8',
                fontWeight: 700,
              }}
            >
              Actualizando análisis. Se mantienen
              temporalmente los últimos datos
              confirmados.
            </Typography>
          </Paper>
        )}

        {hasOverviewRefreshError && (
          <Alert severity="warning">
            No se ha podido actualizar la selección
            analítica. Se mantienen los últimos datos
            confirmados para no perder el contexto de
            trabajo.
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            // PRC-032: filtros globales fijos al desplazarse.
            position: 'sticky',
            top: 8,
            zIndex: 5,
            px: 2,
            py: 1,
            bgcolor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              lg: 'row',
            }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{
              xs: 'stretch',
              lg: 'center',
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={2}
              alignItems={{
                xs: 'stretch',
                md: 'center',
              }}
            >
              <MarginCenterTemporalContextBar
                selection={overview.selection}
                dataset={overview.dataset}
                compact
              />

              <Box
                sx={{
                  pl: {
                    xs: 0,
                    md: 0.5,
                  },
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#64748b',
                    lineHeight: 1.1,
                  }}
                >
                  Último dato disponible
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 750,
                    mt: 0.25,
                  }}
                >
                  {formatIsoDate(
                    overview.dataset
                      .last_transaction_date,
                  )}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{
                xs: 'flex-start',
                lg: 'flex-end',
              }}
              sx={{
                ml: {
                  xs: 0,
                  lg: 'auto',
                },
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <SearchRoundedIcon
                    fontSize="small"
                  />
                }
                disabled={
                  observedSituations.length === 0
                }
                onClick={(event) =>
                  setNoticeAnchorEl(
                    event.currentTarget,
                  )
                }
                sx={{
                  minWidth: 118,
                  textTransform: 'none',
                  color:
                    observedSituations.length > 0
                      ? '#7dd3fc'
                      : '#64748b',
                  borderColor:
                    observedSituations.length > 0
                      ? 'rgba(56,189,248,0.28)'
                      : 'rgba(148,163,184,0.16)',
                  bgcolor:
                    observedSituations.length > 0
                      ? 'rgba(14,165,233,0.05)'
                      : 'transparent',
                  fontWeight: 800,
                }}
              >
                Situaciones · {observedSituations.length}
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <NotificationsNoneRoundedIcon
                    fontSize="small"
                  />
                }
                onClick={() =>
                  setAttentionCenterOpen(true)
                }
                sx={{
                  minWidth: 108,
                  textTransform: 'none',
                  color:
                    commercialAttentionCount > 0
                      ? '#fbbf24'
                      : '#7dd3fc',
                  borderColor:
                    commercialAttentionCount > 0
                      ? 'rgba(245,158,11,0.34)'
                      : 'rgba(56,189,248,0.24)',
                  bgcolor:
                    commercialAttentionCount > 0
                      ? 'rgba(120,53,15,0.08)'
                      : 'rgba(14,165,233,0.04)',
                  fontWeight: 800,
                }}
              >
                Avisos · {
                  attentionUnavailable
                    ? '—'
                    : attentionPending
                      ? '…'
                      : commercialAttentionCount
                }
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Popover
          open={noticesOpen}
          anchorEl={noticeAnchorEl}
          onClose={() =>
            setNoticeAnchorEl(null)
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: 390,
                maxWidth:
                  'calc(100vw - 32px)',
                borderRadius: 2.5,
                border:
                  '1px solid rgba(148,163,184,0.18)',
                bgcolor: '#0f172a',
                backgroundImage: 'none',
                boxShadow:
                  '0 18px 50px rgba(2,6,23,0.34)',
              },
            },
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: '#f8fafc',
                fontWeight: 850,
              }}
            >
              Situaciones observadas
            </Typography>

            <Stack
              spacing={0.65}
              sx={{ mt: 1 }}
            >
              {observedSituations.map(
                (situation) => (
                  <Box
                    component={
                      situation.key === 'destruction-of-margin'
                        ? 'div'
                        : ButtonBase
                    }
                    key={situation.key}
                    onClick={
                      situation.key === 'destruction-of-margin'
                        ? undefined
                        : () => {
                          setNoticeAnchorEl(null);
                          openSituationInExplorer(situation);
                        }
                    }
                    sx={{
                      width: '100%',
                      display: 'block',
                      textAlign: 'left',
                      px: 1.15,
                      py: 0.9,
                      borderRadius: 1.75,
                      border:
                        '1px solid rgba(148,163,184,0.12)',
                      bgcolor:
                        'rgba(2,6,23,0.24)',
                      cursor:
                        situation.key === 'destruction-of-margin'
                          ? 'default'
                          : 'pointer',
                      ...(
                        situation.key !== 'destruction-of-margin'
                          ? {
                            '&:hover': {
                              bgcolor:
                                'rgba(8,47,73,0.18)',
                              borderColor:
                                'rgba(56,189,248,0.28)',
                            },
                          }
                          : {}
                      ),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 800,
                            textTransform:
                              'uppercase',
                          }}
                        >
                          {situation.scope}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 800,
                            mt: 0.1,
                          }}
                        >
                          {situation.label}
                        </Typography>

                        {situation.key === 'destruction-of-margin' && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#64748b',
                              mt: 0.25,
                              lineHeight: 1.3,
                            }}
                          >
                            Detalle transaccional pendiente
                          </Typography>
                        )}
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          flexShrink: 0,
                        }}
                      >
                        {situation.value}
                      </Typography>
                    </Stack>
                  </Box>
                ),
              )}
            </Stack>
          </Box>
        </Popover>



        {current.resolved_cost_rows === 0
          && current.transaction_count > 0 && (
          <Alert severity="info">
            Hay actividad comercial en el periodo,
            pero no existen operaciones con coste
            resuelto. Los ingresos y la actividad
            siguen siendo analizables; el margen no
            es calculable.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          {metricCards.map((metric) => (
            <MetricCard
              key={metric.key}
              label={metric.label}
              value={metric.value}
              detail={metric.detail}
              comparisonValue={
                metric.comparisonValue
              }
              comparisonDetail={
                metric.comparisonDetail
              }
              comparisonLabel={
                metric.comparisonLabel
              }
              comparisonDirection={
                metric.comparisonDirection
              }
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(0, 1.65fr) minmax(300px, 0.72fr)',
            },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              border:
                '1px solid rgba(148,163,184,0.14)',
              background:
                'linear-gradient(180deg, rgba(18,27,47,0.68) 0%, rgba(10,18,33,0.84) 100%)',
              boxShadow:
                '0 14px 34px rgba(2,6,23,0.16)',
              minWidth: 0,
            }}
          >
            <Stack spacing={1.5}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={1.5}
              alignItems={{
                xs: 'stretch',
                md: 'flex-start',
              }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 800,
                  }}
                >
                  Evolución temporal
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    mt: 0.25,
                  }}
                >
                  La comparación se alinea por posición
                  temporal equivalente. El eje X
                  representa únicamente el periodo
                  actual.
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.8 }}
                >
                  <Stack
                    direction="row"
                    spacing={0.6}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 2,
                        bgcolor: '#38bdf8',
                        borderRadius: 99,
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8' }}
                    >
                      Actual ·{' '}
                      {formatIsoDate(
                        overview.selection.start_date,
                      )}
                      {' — '}
                      {formatIsoDate(
                        overview.selection.end_date,
                      )}
                    </Typography>
                  </Stack>

                  {overview.selection
                    .comparison_mode !== 'none'
                    && overview.selection
                      .comparison_start_date
                    && overview.selection
                      .comparison_end_date && (
                    <Stack
                      direction="row"
                      spacing={0.6}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 18,
                          borderTop:
                            '2px dashed #94a3b8',
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b' }}
                      >
                        Comparación ·{' '}
                        {formatIsoDate(
                          overview.selection
                            .comparison_start_date,
                        )}
                        {' — '}
                        {formatIsoDate(
                          overview.selection
                            .comparison_end_date,
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {selectedChartMetric && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={selectedChartMetric.key}
                  onChange={(
                    _,
                    nextMetric: string | null,
                  ) => {
                    if (!nextMetric) {
                      return;
                    }

                    const nextSearchParams =
                      new URLSearchParams(
                        searchParams,
                      );

                    nextSearchParams.set(
                      'metric',
                      nextMetric,
                    );

                    setSearchParams(
                      nextSearchParams,
                    );
                  }}
                  sx={{
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    '& .MuiToggleButton-root': {
                      px: 1.1,
                      py: 0.45,
                      textTransform: 'none',
                      color: '#94a3b8',
                      borderColor:
                        'rgba(148,163,184,0.18)',
                      '&.Mui-selected': {
                        color: '#f8fafc',
                        bgcolor:
                          'rgba(56,189,248,0.12)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor:
                          'rgba(56,189,248,0.16)',
                      },
                    },
                  }}
                >
                  {availableChartMetrics.map(
                    (metric) => (
                      <ToggleButton
                        key={metric.key}
                        value={metric.key}
                      >
                        {metric.label}
                      </ToggleButton>
                    ),
                  )}
                </ToggleButtonGroup>
              )}
            </Stack>

            {chartSeriesData.length > 0 ? (
              <Box
                sx={{
                  mt: 0.5,
                  width: '100%',
                  height: 300,
                }}
              >
                <ResponsiveContainer>
                  <LineChart
                    data={chartSeriesData}
                    margin={{
                      top: 8,
                      right: 12,
                      bottom: 4,
                      left: 4,
                    }}
                  >
                    <CartesianGrid
                      vertical={false}
                      stroke="rgba(148,163,184,0.12)"
                      strokeDasharray="3 5"
                    />

                    <XAxis
                      dataKey="label"
                      axisLine={{
                        stroke:
                          'rgba(148,163,184,0.20)',
                      }}
                      tickLine={false}
                      minTickGap={46}
                      tick={{
                        fill: '#64748b',
                        fontSize: 11,
                      }}
                    />

                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      width={72}
                      tick={{
                        fill: '#64748b',
                        fontSize: 11,
                      }}
                      tickFormatter={(value) =>
                        formatSelectedChartValue(
                          Number(value),
                        )
                      }
                    />

                    <RechartsTooltip
                      cursor={{
                        stroke:
                          'rgba(56,189,248,0.28)',
                        strokeWidth: 1,
                      }}
                      content={({
                        active,
                        payload,
                      }) => {
                        if (
                          !active
                          || !payload
                          || payload.length === 0
                        ) {
                          return null;
                        }

                        const point:
                          OverviewChartPoint | undefined =
                          payload[0]?.payload;

                        if (!point) {
                          return null;
                        }

                        const hasComparison =
                          point.comparison_value
                            !== null;

                        const delta =
                          hasComparison
                          && point.current_value
                            !== null
                            ? (
                              point.current_value
                              - (
                                point.comparison_value
                                ?? 0
                              )
                            )
                            : null;

                        const deltaPct =
                          delta !== null
                          && point.comparison_value
                            !== null
                          && point.comparison_value
                            !== 0
                            ? (
                              delta
                              / Math.abs(
                                point.comparison_value,
                              )
                              * 100
                            )
                            : null;

                        return (
                          <Paper
                            elevation={8}
                            sx={{
                              minWidth: 250,
                              p: 1.4,
                              bgcolor: '#0b1220',
                              border:
                                '1px solid rgba(148,163,184,0.20)',
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                fontWeight: 800,
                              }}
                            >
                              Posición temporal{' '}
                              {point.bucket_index + 1}
                            </Typography>

                            <Stack
                              spacing={1}
                              sx={{ mt: 0.8 }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#38bdf8',
                                    fontWeight: 800,
                                  }}
                                >
                                  Periodo actual
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: '#64748b',
                                  }}
                                >
                                  {formatShortIsoDate(
                                    point.current_start,
                                  )}
                                  {' — '}
                                  {formatShortIsoDate(
                                    point.current_end,
                                  )}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 850,
                                    mt: 0.1,
                                  }}
                                >
                                  {formatSelectedChartValue(
                                    point.current_value,
                                  )}
                                </Typography>
                              </Box>

                              {hasComparison && (
                                <Box
                                  sx={{
                                    pt: 0.8,
                                    borderTop:
                                      '1px solid rgba(148,163,184,0.10)',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#94a3b8',
                                      fontWeight: 800,
                                    }}
                                  >
                                    Periodo comparado
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: '#64748b',
                                    }}
                                  >
                                    {formatShortIsoDate(
                                      point
                                        .comparison_start,
                                    )}
                                    {' — '}
                                    {formatShortIsoDate(
                                      point
                                        .comparison_end,
                                    )}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#e2e8f0',
                                      fontWeight: 850,
                                      mt: 0.1,
                                    }}
                                  >
                                    {formatSelectedChartValue(
                                      point
                                        .comparison_value,
                                    )}
                                  </Typography>
                                </Box>
                              )}

                              {delta !== null && (
                                <Box
                                  sx={{
                                    pt: 0.8,
                                    borderTop:
                                      '1px solid rgba(148,163,184,0.10)',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                    }}
                                  >
                                    Variación equivalente
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        delta > 0
                                          ? '#34d399'
                                          : delta < 0
                                            ? '#fb7185'
                                            : '#94a3b8',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {selectedChartMetric
                                      ?.isPercentage
                                      ? `${
                                        formatSignedNumber(
                                          delta,
                                          1,
                                        )
                                      } pp`
                                      : formatSignedNumber(
                                        delta,
                                        selectedChartMetric
                                          ?.maximumFractionDigits
                                        ?? 2,
                                      )}

                                    {deltaPct !== null
                                      && !selectedChartMetric
                                        ?.isPercentage
                                      ? ` · ${
                                        formatSignedPercentage(
                                          deltaPct,
                                        )
                                      }`
                                      : ''}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        );
                      }}
                    />

                    <Line
                      type="monotone"
                      dataKey="current_value"
                      name="Periodo actual"
                      stroke="#38bdf8"
                      strokeWidth={2.4}
                      dot={false}
                      activeDot={{
                        r: 4,
                        strokeWidth: 0,
                        fill: '#7dd3fc',
                      }}
                      connectNulls={false}
                    />

                    {chartSeriesData.some(
                      (point) =>
                        point.comparison_value !== null,
                    ) && (
                      <Line
                        type="monotone"
                        dataKey="comparison_value"
                        name="Comparación"
                        stroke="#94a3b8"
                        strokeWidth={1.8}
                        strokeDasharray="6 5"
                        strokeOpacity={0.72}
                        dot={false}
                        activeDot={{
                          r: 3,
                          strokeWidth: 0,
                          fill: '#cbd5e1',
                        }}
                        connectNulls={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: '#64748b' }}
              >
                No hay serie temporal disponible para
                la métrica y selección actuales.
              </Typography>
            )}
            </Stack>
          </Paper>

          <CommercialQueryPanel
            context={commercialQueryContext}
            currencyCode={currencyCode}
            hasPartialBucket={
              chartSeriesData.some(
                (point) =>
                  point.is_partial_bucket,
              )
            }
            onOpenCustomer={
              openCustomerContext
            }
            onOpenRelationship={
              openRelationship
            }
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          {/* PRC-048 (boceto S1): «Actividad de compra» con la lectura de Cadencia, a todo el ancho. */}
          <PurchaseActivityBlock
            onOpenRow={openPurchaseActivityRow}
            cadenceHref={(lifecycle) => withOverviewOrigin(`/margin-center/control-comercial/avisos/cadencia?lifecycle=${lifecycle}`)}
          />

        <Paper
          elevation={0}
          sx={{
            height: '100%',
            p: 1.75,
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            minWidth: 0,
          }}
        >
          <Stack spacing={1.35}>
            <Stack
              spacing={1.35}
              sx={{
                minHeight: {
                  xs: 'auto',
                  xl: 178,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 850,
                      letterSpacing: 0.9,
                    }}
                  >
                    Clientes
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      mt: 0.1,
                    }}
                  >
                    Clientes con cambios
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#64748b',
                      mt: 0.25,
                      lineHeight: 1.4,
                    }}
                  >
                    Vista rápida según el criterio
                    seleccionado. El análisis completo
                    se realiza en la vista de clientes.
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="text"
                  onClick={
                    openCustomerChangesInExplorer
                  }
                  sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    fontWeight: 850,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Ver clientes →
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={0.55}
                useFlexGap
                flexWrap="wrap"
              >
                {CUSTOMER_CHANGE_OPTIONS.map(
                  (option) => {
                    const isActive =
                      customerChangeOption.value
                      === option.value;

                    const directionLabel =
                      isActive
                      && option.toggleDirection
                        ? (
                          customerChangeSortDirection
                            === 'desc'
                            ? ' ↓'
                            : ' ↑'
                        )
                        : '';

                    return (
                      <Button
                        key={option.value}
                        size="small"
                        variant={
                          isActive
                            ? 'outlined'
                            : 'text'
                        }
                        disabled={
                          overview.selection
                            .comparison_mode === 'none'
                          && option.needsComparison
                        }
                        onClick={() => {
                          const nextSearchParams =
                            new URLSearchParams(
                              searchParams,
                            );

                          if (
                            isActive
                            && option.toggleDirection
                          ) {
                            nextSearchParams.set(
                              'customerDirection',
                              customerChangeSortDirection
                                === 'desc'
                                ? 'asc'
                                : 'desc',
                            );
                          } else {
                            nextSearchParams.set(
                              'customerCriterion',
                              option.value,
                            );

                            if (
                              option.toggleDirection
                            ) {
                              nextSearchParams.set(
                                'customerDirection',
                                option
                                  .defaultSortDirection,
                              );
                            } else {
                              nextSearchParams.delete(
                                'customerDirection',
                              );
                            }
                          }

                          nextSearchParams.delete(
                            'customer',
                          );

                          nextSearchParams.delete(
                            'customerSignal',
                          );

                          nextSearchParams.delete(
                            'customerCursor',
                          );

                          setSearchParams(
                            nextSearchParams,
                          );
                        }}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          py: 0.4,
                          textTransform: 'none',
                          fontSize: '0.73rem',
                          fontWeight:
                            isActive ? 850 : 650,
                          color:
                            isActive
                              ? '#e2e8f0'
                              : '#94a3b8',
                          borderColor:
                            isActive
                              ? 'rgba(56,189,248,0.34)'
                              : 'transparent',
                          bgcolor:
                            isActive
                              ? 'rgba(56,189,248,0.08)'
                              : 'transparent',
                        }}
                      >
                        {option.label}
                        {directionLabel}
                      </Button>
                    );
                  },
                )}
              </Stack>
            </Stack>

            {customerChangesQuery.isFetching && (
              <LinearProgress />
            )}

            {customerChangesQuery.isError && (
              <Alert severity="warning">
                No se han podido cargar los clientes
                para este criterio.
              </Alert>
            )}

            {customerChangesQuery.data?.status
              === 'no_results' && (
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  py: 2,
                }}
              >
                No hay clientes que cumplan este
                criterio en la selección actual.
              </Typography>
            )}

            {customerChangesQuery.data?.status
              === 'ok' && (
              <Stack
                spacing={0.6}
                sx={{
                  // PRC-032: sin barra vertical propia dentro de la página.
                  overflowX: 'hidden',
                  pr: 0.65,

                  '&::-webkit-scrollbar': {
                    width: 7,
                  },

                  '&::-webkit-scrollbar-track': {
                    background:
                      'rgba(15,23,42,0.45)',
                    borderRadius: 99,
                  },

                  '&::-webkit-scrollbar-thumb': {
                    background:
                      'rgba(100,116,139,0.48)',
                    borderRadius: 99,
                  },

                  '&::-webkit-scrollbar-thumb:hover': {
                    background:
                      'rgba(148,163,184,0.58)',
                  },
                }}
              >
                {customerChangesQuery.data.rows.slice(0, showAllChanges ? undefined : OVERVIEW_LIST_PREVIEW).map(
                  (row: MarginCenterExplorerRow) => {
                    let value = '—';
                    let detail = '';

                    if (
                      customerChangeOption.value
                        === 'revenue_change'
                    ) {
                      value =
                        formatSignedPercentage(
                          row.changes
                            ?.revenue_delta_pct,
                        );

                      detail =
                        `Ingresos ${
                          formatCurrency(
                            row.current.revenue,
                            currencyCode,
                          )
                        }`;
                    } else if (
                      customerChangeOption.value
                        === 'margin_change'
                    ) {
                      value =
                        row.changes
                          ?.margin_percentage_delta_points
                          !== null
                        && row.changes
                          ?.margin_percentage_delta_points
                          !== undefined
                          ? `${
                            formatSignedNumber(
                              row.changes
                                .margin_percentage_delta_points,
                              1,
                            )
                          } pp`
                          : '—';

                      detail =
                        `Margen actual ${
                          formatPercentage(
                            row.current
                              .margin_percentage,
                          )
                        }`;
                    } else if (
                      customerChangeOption.value
                        === 'negative_margin'
                    ) {
                      value =
                        formatCurrency(
                          row.current.margin_amount,
                          currencyCode,
                        );

                      detail =
                        `Margen % ${
                          formatPercentage(
                            row.current
                              .margin_percentage,
                          )
                        }`;
                    } else {
                      value = 'Sin actividad';

                      detail =
                        row.comparison
                          ? (
                            `Ingresos en comparación ${
                              formatCurrency(
                                row.comparison.revenue,
                                currencyCode,
                              )
                            }`
                          )
                          : 'Sin base comparable';
                    }

                    return (
                      <ButtonBase
                        key={row.entity.key}
                        onClick={() => {
                          if (
                            row.entity.customer_id
                          ) {
                            openCustomerContext(
                              row.entity.customer_id,
                            );
                          }
                        }}
                        sx={{
                          width: '100%',
                          display: 'block',
                          textAlign: 'left',
                          px: 1,
                          py: 0.85,
                          borderRadius: 1.75,
                          border:
                            '1px solid rgba(148,163,184,0.12)',
                          bgcolor:
                            'rgba(2,6,23,0.22)',

                          '&:hover': {
                            bgcolor:
                              'rgba(8,47,73,0.18)',
                            borderColor:
                              'rgba(56,189,248,0.28)',
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                color: '#e2e8f0',
                                fontWeight: 800,
                              }}
                            >
                              {row.entity.label}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: '#64748b',
                                mt: 0.1,
                              }}
                            >
                              {detail}
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 900,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {value}
                          </Typography>
                        </Stack>
                      </ButtonBase>
                    );
                  },
                )}
                {customerChangesQuery.data.rows.length > OVERVIEW_LIST_PREVIEW && (
                  <Button size="small" onClick={() => setShowAllChanges((v) => !v)} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800 }}>
                    {showAllChanges ? 'Ver menos' : `Ver todos (${customerChangesQuery.data.rows.length})`}
                  </Button>
                )}
              </Stack>
            )}

            {customerChangesQuery.data?.status
              === 'ok' && (
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={1}
                alignItems={{
                  xs: 'stretch',
                  sm: 'center',
                }}
                justifyContent="space-between"
              >
                <Typography
                  variant="caption"
                  sx={{ color: '#64748b' }}
                >
                  Mostrando{' '}
                  {
                    customerChangesQuery.data
                      .page.returned_count
                  } clientes en esta página.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                >
                  {customerChangesCursor && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const nextSearchParams =
                          new URLSearchParams(
                            searchParams,
                          );

                        nextSearchParams.delete(
                          'customerCursor',
                        );

                        setSearchParams(
                          nextSearchParams,
                        );
                      }}
                    >
                      Volver al inicio
                    </Button>
                  )}

                  {customerChangesQuery.data
                    .page.has_more
                    && customerChangesQuery.data
                      .page.next_cursor && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const nextSearchParams =
                          new URLSearchParams(
                            searchParams,
                          );

                        nextSearchParams.set(
                          'customerCursor',
                          customerChangesQuery.data
                            ?.page.next_cursor
                          ?? '',
                        );

                        setSearchParams(
                          nextSearchParams,
                        );
                      }}
                    >
                      Siguiente
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>
        

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(0, 1.6fr) minmax(320px, 0.85fr)',
            },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2,
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.18)',
              bgcolor:
                'rgba(15,23,42,0.66)',
              minWidth: 0,
            }}
          >
            <Stack
              spacing={1.5}
              sx={{
                height: '100%',
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 850,
                    letterSpacing: 0.9,
                  }}
                >
                  Seguimientos
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    mt: 0.1,
                  }}
                >
                  Trabajo comercial abierto
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#64748b',
                    mt: 0.25,
                    lineHeight: 1.45,
                  }}
                >
                  Situaciones que una persona ha
                  decidido investigar o gestionar.
                  Ninguna señal crea un seguimiento
                  automáticamente.
                </Typography>
              </Box>

              {followUpPortfolioQuery.isFetching && (
                <LinearProgress />
              )}

              {followUpPortfolioQuery.isError && (
                <Alert severity="warning">
                  No se han podido cargar los
                  Seguimientos. El resto del Overview
                  permanece disponible.
                </Alert>
              )}

              {followUpPortfolioQuery.data
                && (
                  followUpPortfolioQuery.data.status
                    === 'ok'
                  || followUpPortfolioQuery.data.status
                    === 'no_results'
                ) && (
                  <>
                    {followUpPortfolioQuery.data
                      .summary.open_count > 0 && (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs:
                              'repeat(2, minmax(0, 1fr))',
                            sm:
                              'repeat(4, minmax(0, 1fr))',
                          },
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            Abiertos
                          </Typography>

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                            }}
                          >
                            {formatNumber(
                              followUpPortfolioQuery
                                .data.summary.open_count,
                              0,
                            )}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            Nuevos
                          </Typography>

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                            }}
                          >
                            {formatNumber(
                              followUpPortfolioQuery
                                .data.summary.new_count,
                              0,
                            )}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            En revisión
                          </Typography>

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                            }}
                          >
                            {formatNumber(
                              followUpPortfolioQuery
                                .data.summary
                                .in_review_count,
                              0,
                            )}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            Con próxima revisión
                          </Typography>

                          <Typography
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 850,
                            }}
                          >
                            {formatNumber(
                              followUpPortfolioQuery
                                .data.summary
                                .with_next_review_count,
                              0,
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {followUpPortfolioQuery.data
                      .items.length > 0 ? (
                      <Stack
                        spacing={0.65}
                        sx={{
                          // PRC-032: sin barra vertical propia dentro de la página.
                          pr: 0.5,

                          '&::-webkit-scrollbar': {
                            width: 6,
                          },

                          '&::-webkit-scrollbar-thumb':
                            {
                              background:
                                'rgba(100,116,139,0.45)',
                              borderRadius: 99,
                            },
                        }}
                      >
                        {followUpPortfolioQuery.data
                          .items.map(
                            (
                              item:
                                MarginCenterFollowUpPortfolioItem,
                            ) => (
                              <ButtonBase
                                key={item.id}
                                onClick={() =>
                                  openFollowUpRelationship(
                                    item,
                                  )
                                }
                                sx={{
                                  width: '100%',
                                  display: 'block',
                                  textAlign: 'left',
                                  px: 1.1,
                                  py: 0.9,
                                  borderRadius: 1.75,
                                  border:
                                    '1px solid rgba(148,163,184,0.12)',
                                  bgcolor:
                                    'rgba(2,6,23,0.22)',

                                  '&:hover': {
                                    bgcolor:
                                      'rgba(8,47,73,0.18)',
                                    borderColor:
                                      'rgba(56,189,248,0.28)',
                                  },
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1.25}
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Box
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      noWrap
                                      sx={{
                                        color: '#e2e8f0',
                                        fontWeight: 850,
                                      }}
                                    >
                                      {item.customer_label}
                                      {' · '}
                                      {item.product_label}
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: '#64748b',
                                        mt: 0.15,
                                      }}
                                    >
                                      {
                                        getFollowUpStatusLabel(
                                          item.status,
                                        )
                                      }

                                      {item.next_review_at
                                        ? (
                                          <>
                                            {' · Revisión '}
                                            {formatIsoDate(
                                              item
                                                .next_review_at
                                                .slice(0, 10),
                                            )}
                                          </>
                                        )
                                        : (
                                          ' · Sin próxima revisión'
                                        )}
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        display: 'block',
                                        color: '#94a3b8',
                                        mt: 0.2,
                                      }}
                                    >
                                      {item.next_action
                                        ?? (
                                          'Sin siguiente acción '
                                          + 'registrada'
                                        )}
                                    </Typography>
                                  </Box>

                                  <Box
                                    aria-hidden="true"
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      flexShrink: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent:
                                        'center',
                                      borderRadius: 1.5,
                                      color: '#38bdf8',
                                      border:
                                        '1px solid rgba(56,189,248,0.28)',
                                      bgcolor:
                                        'rgba(56,189,248,0.06)',
                                    }}
                                  >
                                    <SearchRoundedIcon
                                      sx={{
                                        fontSize: 18,
                                      }}
                                    />
                                  </Box>
                                </Stack>
                              </ButtonBase>
                            ),
                          )}
                      </Stack>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1,
                          minHeight: 150,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 2,
                          border:
                            '1px solid rgba(148,163,184,0.12)',
                          bgcolor:
                            'rgba(2,6,23,0.22)',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#e2e8f0',
                              fontWeight: 800,
                            }}
                          >
                            No hay Seguimientos abiertos.
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#64748b',
                              lineHeight: 1.5,
                              mt: 0.45,
                            }}
                          >
                            Los Seguimientos se crean
                            explícitamente desde una
                            relación cliente-producto
                            cuando una persona decide
                            trabajar sobre ella.
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </>
                )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2,
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.18)',
              bgcolor:
                'rgba(15,23,42,0.66)',
              minWidth: 0,
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 850,
                    letterSpacing: 0.9,
                  }}
                >
                  Actividad económica
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    mt: 0.1,
                  }}
                >
                  Precio, volumen y coste
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#64748b',
                    mt: 0.25,
                    lineHeight: 1.45,
                  }}
                >
                  Variables observadas en el periodo
                  actual y su referencia de comparación.
                  Describe movimientos, no sus causas.
                </Typography>
              </Box>

              <Stack spacing={0.8}>
                {priceVolumeCostMetrics.map(
                  (metric) => (
                    <Box
                      key={metric.key}
                      sx={{
                        px: 1.25,
                        py: 1.05,
                        borderRadius: 1.75,
                        border:
                          '1px solid rgba(148,163,184,0.10)',
                        bgcolor:
                          'rgba(2,6,23,0.22)',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 750,
                          }}
                        >
                          {metric.label}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {metric.currentValue}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ mt: 0.25 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          {metric.comparisonValue
                            ? (
                              `Comparado: ${
                                metric.comparisonValue
                              }`
                            )
                            : 'Sin comparación activa'}
                        </Typography>

                        {metric.comparisonChange
                          !== null && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#94a3b8',
                              fontWeight: 800,
                            }}
                          >
                            ·{' '}
                            {formatSignedPercentage(
                              metric.comparisonChange,
                            )}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  ),
                )}

                {current.unresolved_cost_rows > 0 && (
                  <Box
                    sx={{
                      mt: 0.4,
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.75,
                      border:
                        '1px solid rgba(148,163,184,0.10)',
                      bgcolor:
                        'rgba(2,6,23,0.16)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#94a3b8',
                        fontWeight: 800,
                      }}
                    >
                      Calidad del dato
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#64748b',
                        mt: 0.25,
                        lineHeight: 1.45,
                      }}
                    >
                      {formatNumber(
                        current.unresolved_cost_rows,
                        0,
                      )}
                      {' '}
                      {current.unresolved_cost_rows === 1
                        ? 'operación'
                        : 'operaciones'}
                      {' sin coste resuelto · '}
                      {formatCurrency(
                        current.unresolved_revenue,
                        currencyCode,
                      )}
                      {' fuera del cálculo de margen.'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <MarginCenterAttentionCenterDrawer
          open={attentionCenterOpen}
          initialCategory={
            cadenceAttentionCount > 0
              ? 'cadence'
              : 'follow_up_due'
          }
          onClose={() =>
            setAttentionCenterOpen(false)}
          onOpenCadenceRelationship={
            openCadenceAttentionRelationship
          }
          onOpenFollowUp={(followUpId) => {
            const next =
              new URLSearchParams({
                context: 'global',
                case: followUpId,
                // PRC-028: un solo «Volver» lleva a Overview.
                caseReturnTo:
                  `/margin-center/overview${
                    searchParams.toString()
                      ? `?${searchParams.toString()}`
                      : ''
                  }`,
              });

            navigate(
              `/margin-center/control-comercial?${next.toString()}`,
            );
          }}
        />

        <CustomerQuickContextDrawer
          customerId={selectedCustomerId}
          params={customerContextParams}
          currencyCode={currencyCode}
          onClose={closeCustomerContext}
          onOpenFull={openFullCustomerContext}
        />

        {selectedRelationshipCustomerId
          && selectedRelationshipProductId && (
          <CustomerRelationshipWorkspace
            customerId={
              selectedRelationshipCustomerId
            }
            productId={
              selectedRelationshipProductId
            }
            startDate={
              overview.selection.start_date
            }
            endDate={
              overview.selection.end_date
            }
            comparisonMode={
              overview.selection.comparison_mode
            }
            comparisonStartDate={
              overview.selection
                .comparison_start_date
            }
            comparisonEndDate={
              overview.selection
                .comparison_end_date
            }
            timeGrain={
              overview.selection.time_grain
            }
            initialMode={
              selectedRelationshipCadenceOccurrence
                ? 'follow_up'
                : 'analysis'
            }
            initialFollowUpId={selectedRelationshipFollowUpId}
            cadenceAttentionOccurrenceDate={
              selectedRelationshipCadenceOccurrence
            }
            onFollowUpIdChange={selectRelationshipFollowUp}
            onClose={
              closeCadenceRelationship
            }
          />
        )}
      </Stack>
    </Box>
  );
};


export default MarginCenterOverviewPage;